/**
 * The device layer: keyboard listeners, gamepad polling, and the per-frame
 * edges the game actually asks about.
 *
 * `inputMap.js` does the translation and is pure. This is the part that has to
 * touch the browser, and it is kept thin for exactly that reason — everything
 * here is about the awkward realities of real devices:
 *
 *   - A key pressed and released inside one frame must still count. Polling a
 *     held-key set would miss it entirely, so a keydown also lands in a sticky
 *     set that survives until the next poll consumes it. A tap shorter than a
 *     frame registers for exactly one frame, which is what a fighting game
 *     needs from a light attack.
 *   - A `Gamepad` object is a snapshot, not a live handle. Chrome hands back a
 *     fresh one from every `getGamepads()` call and the one you kept goes
 *     stale, so the pad is re-read every poll. That is also what makes hot-plug
 *     fall out for free: unplug the controller mid-fight and the next poll
 *     simply finds nothing, instead of leaving `block` held down forever.
 *   - Losing window focus releases everything. Otherwise holding W and
 *     alt-tabbing away leaves a fighter walking into the fog, with no keyup
 *     ever coming to stop them.
 *   - **A key typed into a text field belongs to the field.** See `isTyping`.
 */

import {
  ACTIONS,
  boundKeys,
  combine,
  idleInput,
  resolveGamepad,
  resolveKeyboard,
  validateBindings,
} from './inputMap.js';

export { ACTIONS };

/**
 * The `<input>` types that are **not** text entry.
 *
 * A negative list rather than a positive one, deliberately. A positive list of
 * `text`, `search`, `email`… is a list somebody has to remember to add to, and
 * the failure when they do not is a field that silently eats letters. An
 * unknown or future type falls through to "this takes characters", which errs
 * toward letting a person type — the safe direction for this particular bug.
 */
const NOT_TEXTUAL = Object.freeze(
  new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
  ])
);

/**
 * Whether `node` is something a person is typing into.
 *
 * The create screen asks for a fighter's name, `KeyW` is bound to forward, and
 * `onKeyDown` used to call `preventDefault` on every bound key — so **"Wally
 * Kim Dawes" arrived as "yime"**. Every letter bound to the game was eaten
 * before the field saw it, and the name field had no way to know.
 *
 * The fix belongs here rather than in the screen. A screen that had to remember
 * to guard its own inputs is a screen that will forget, and the next text field
 * anybody adds would arrive broken in exactly the same way — which is why this
 * asks what kind of *element* has focus rather than which screen is up.
 *
 * Deliberately **not** extended to buttons. A focused `<button>` handles Space
 * and Enter itself, and yielding those to it would be defensible — but every
 * screen already handles its own Enter and Space, the debug overlay is full of
 * buttons that keep focus after a click, and a fighter who stopped answering
 * WASD because somebody had clicked something is a worse bug than the one being
 * fixed. Text entry is unambiguous; a button is not.
 */
/**
 * Whether `node` is editable, asked two ways.
 *
 * `isContentEditable` is the right question — it is computed, so it inherits
 * from an editable ancestor — and **jsdom does not implement it**, which makes
 * it a property the tests cannot see. The attribute is explicit rather than
 * computed and answers everywhere, so both are asked and either is enough. An
 * empty `contenteditable=""` means true, which is why the comparison is against
 * `"false"` rather than for `"true"`.
 */
function contentEditable(node) {
  if (node.isContentEditable === true) return true;
  const said = typeof node.getAttribute === 'function' ? node.getAttribute('contenteditable') : null;
  return said !== null && said !== undefined && said.toLowerCase() !== 'false';
}

export function isTyping(node) {
  if (!node || typeof node !== 'object') return false;

  const tag = node.tagName;
  const editable =
    contentEditable(node) ||
    tag === 'TEXTAREA' ||
    (tag === 'INPUT' && !NOT_TEXTUAL.has(String(node.type ?? 'text').toLowerCase()));
  if (!editable) return false;

  /**
   * And it has to be on screen.
   *
   * Belt and braces. Chrome does blur a field when an ancestor is hidden, so
   * this changes nothing today — but the failure it guards against is the
   * nastiest one available here: a focused field inside a screen that has been
   * put away would swallow **every** bound key, for the rest of the session,
   * silently. `hidden` is how every screen in this project hides itself, and
   * `closest` is a cheap answer that needs no layout.
   */
  return typeof node.closest === 'function' ? !node.closest('[hidden]') : true;
}

export function createInput(
  bindings,
  { target = globalThis, gamepads = () => globalThis.navigator?.getGamepads?.() ?? [] } = {}
) {
  validateBindings(bindings);
  const swallow = boundKeys(bindings);

  /** Physically down right now. */
  const down = new Set();
  /** Pressed since the last poll, whether or not it is still down. */
  const tapped = new Set();

  /** Index the pad announced itself on; re-checked every poll, never trusted. */
  let padIndex = null;
  let padId = null;

  let move = { x: 0, y: 0 };
  let held = new Set();
  let pressed = new Set();
  let released = new Set();

  /**
   * Whether this keystroke is somebody typing rather than somebody playing.
   *
   * Both the event's own target and the document's active element, because they
   * answer different questions and either one is enough. Real typing arrives
   * with the field as the target; an event dispatched at the window while a
   * field has focus — which is what a synthetic one does, and what a stray
   * re-dispatch would do — only shows up in `activeElement`.
   */
  function typing(event) {
    return isTyping(event?.target) || isTyping(target.document?.activeElement);
  }

  function onKeyDown(event) {
    // Auto-repeat is not a new press, and treating it as one would make a held
    // key fire a light attack thirty times a second.
    if (event.repeat) return;
    if (!swallow.has(event.code)) return;
    // Not the game's key: it is a letter in somebody's name.
    if (typing(event)) return;
    // Space scrolls the page and the arrows scroll it too. Only bound keys are
    // swallowed, so the browser's own shortcuts still work.
    event.preventDefault();
    down.add(event.code);
    tapped.add(event.code);
  }

  function onKeyUp(event) {
    if (!swallow.has(event.code)) return;
    // The matching keydown was yielded, so there is nothing of ours to release
    // and nothing to swallow.
    if (typing(event)) return;
    event.preventDefault();
    down.delete(event.code);
  }

  function onBlur() {
    down.clear();
  }

  /**
   * Focus arriving in a text field releases whatever was held.
   *
   * The same reasoning as `onBlur`, one level in: hold W, click into the name
   * field, and the keyup that follows is yielded to the field — so without this
   * the fighter walks forward for the rest of the round with no key ever coming
   * to stop them.
   */
  function onFocusIn(event) {
    if (isTyping(event?.target)) down.clear();
  }

  function onConnect(event) {
    padIndex = event.gamepad?.index ?? null;
  }

  function onDisconnect(event) {
    if (event.gamepad?.index === padIndex) padIndex = null;
  }

  /**
   * The pad we are listening to, freshly read.
   *
   * Prefers the one that announced itself, and falls back to the first one
   * present — Chrome does not fire `gamepadconnected` for a pad that was
   * already plugged in when the page loaded, only once a button is pressed.
   */
  function currentPad() {
    const list = gamepads() ?? [];
    const announced = padIndex === null ? null : list[padIndex];
    const pad = announced ?? [...list].find((candidate) => candidate?.connected) ?? null;
    if (pad && padIndex === null) padIndex = pad.index;
    padId = pad?.id ?? null;
    return pad;
  }

  target.addEventListener?.('keydown', onKeyDown);
  target.addEventListener?.('keyup', onKeyUp);
  target.addEventListener?.('blur', onBlur);
  target.addEventListener?.('focusin', onFocusIn);
  target.addEventListener?.('gamepadconnected', onConnect);
  target.addEventListener?.('gamepaddisconnected', onDisconnect);

  return {
    /** `{ x, y }`, +y forward and +x to the fighter's right. Never longer than 1. */
    get move() {
      return move;
    },
    /** Verbs currently held, in the canonical order. */
    get actions() {
      return ACTIONS.filter((name) => held.has(name));
    },
    /** The pad's id string, or null when nothing is plugged in. */
    get gamepad() {
      return padId;
    },
    get hasGamepad() {
      return padId !== null;
    },

    held: (action) => held.has(action),
    /** True on the single frame the verb went down. */
    pressed: (action) => pressed.has(action),
    /** True on the single frame the verb came back up. */
    released: (action) => released.has(action),

    /**
     * Polls the devices and recomputes the edges. Call once per frame, before
     * anything that reads the input — every `pressed()` answer is fixed at this
     * moment, so two consumers in the same frame cannot disagree about it.
     */
    update() {
      // A key tapped since the last poll counts as held for this frame, even if
      // it is already back up.
      const keys = new Set([...down, ...tapped]);
      tapped.clear();

      const pad = currentPad();
      const next = combine(
        resolveKeyboard(bindings, keys),
        pad ? resolveGamepad(bindings, pad) : idleInput()
      );

      const previous = held;
      held = new Set(next.actions);
      pressed = new Set(next.actions.filter((name) => !previous.has(name)));
      released = new Set([...previous].filter((name) => !held.has(name)));
      move = next.move;
    },

    /** Drops everything held. Used when the game takes control away from the player. */
    clear() {
      down.clear();
      tapped.clear();
      held = new Set();
      pressed = new Set();
      released = new Set();
      move = { x: 0, y: 0 };
    },

    dispose() {
      target.removeEventListener?.('keydown', onKeyDown);
      target.removeEventListener?.('keyup', onKeyUp);
      target.removeEventListener?.('blur', onBlur);
    target.removeEventListener?.('focusin', onFocusIn);
      target.removeEventListener?.('gamepadconnected', onConnect);
      target.removeEventListener?.('gamepaddisconnected', onDisconnect);
    },
  };
}
